import { useTranslation } from 'react-i18next'
import { Mail, MapPin, Phone } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { type RouteKey } from '@/i18n/routes'

const navItems: { key: RouteKey; labelKey: string }[] = [
  { key: 'home', labelKey: 'nav.home' },
  { key: 'services', labelKey: 'nav.services' },
  { key: 'projects', labelKey: 'nav.projects' },
  { key: 'contacts', labelKey: 'nav.contacts' },
]

const legalItems: { key: RouteKey; labelKey: string }[] = [
  { key: 'privacy', labelKey: 'footer.privacy' },
]

export function Footer() {
  const { t } = useTranslation()
  const year = new Date().getFullYear()

  return (
    <footer className="bg-footer-bg text-footer-text">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img
              src="/media/logos/logo.png"
              alt={t('footer.logoAlt')}
              className="h-12 w-auto mb-4"
              width="180"
              height="48"
            />
            <p className="text-sm leading-relaxed max-w-xs">{t('footer.tagline')}</p>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-white font-display font-semibold mb-4">{t('footer.navigation')}</h3>
            <ul className="space-y-2">
              {navItems.map(({ key, labelKey }) => (
                <li key={key}>
                  <LocalizedLink
                    to={key}
                    className="text-sm hover:text-white transition-colors"
                  >
                    {t(labelKey)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contacts */}
          <div>
            <h3 className="text-white font-display font-semibold mb-4">{t('footer.contacts')}</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-brand" />
                <span>
                  {t('contact.address')}
                  <br />
                  {t('contact.city')}
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-brand" />
                <a
                  href={`tel:${String(t('contact.phone')).replace(/\s/g, '')}`}
                  className="hover:text-white transition-colors"
                >
                  {t('contact.phone')}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-brand" />
                <a
                  href={`mailto:${t('contact.email')}`}
                  className="hover:text-white transition-colors"
                >
                  {t('contact.email')}
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-white font-display font-semibold mb-4">{t('footer.legalNotes')}</h3>
            <ul className="space-y-2">
              {legalItems.map(({ key, labelKey }) => (
                <li key={key}>
                  <LocalizedLink
                    to={key}
                    className="text-sm hover:text-white transition-colors"
                  >
                    {t(labelKey)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs">
              P.IVA / C.F. {t('contact.vat')}
              <br />
              {t('contact.rea')}
            </p>
          </div>
        </div>

        <div className="border-t border-white/10 mt-10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs">
          <p>© {year} Pierre Costruzioni S.r.l. — {t('footer.allRightsReserved')}</p>
        </div>
      </div>
    </footer>
  )
}
